import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiCall } from '../../utils/fetchAPI';
import { translateApiMessage } from '../../utils/apiMessages';
import { firstReportQueue, isReportQueue } from '../../constants/reportQueues';

const QUEUE_LIMIT = 50;

/**
 * Everything the moderation page reads and does.
 *
 * Which city is being moderated lives in the URL (`?ville=`), so a moderator
 * of two cities can keep a tab open on each; with one city it is simply that
 * one. The API checks every call against the caller's mandates, so the slug
 * here is a choice of page, never a permission.
 */
export function useModeration() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mandates, setMandates] = useState([]);
  const [mandatesLoaded, setMandatesLoaded] = useState(false);
  const [workload, setWorkload] = useState(null);
  // The queue on screen, with the key of what it holds: the URL changes queue
  // a render before the new one is read, and the reports of one queue must
  // not be shown — or acted on — under the chip of another.
  const [queue, setQueue] = useState({ key: null, items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // The report queue on screen. Named in the URL once chosen; until then, the
  // one the counts say to open first.
  const [reportQueue, setReportQueue] = useState(null);

  const cities = useMemo(
    () => mandates.filter((mandate) => 'ACTIVE' === mandate.status).map((mandate) => mandate.city),
    [mandates]
  );
  const pending = useMemo(() => mandates.filter((mandate) => 'PENDING' === mandate.status), [mandates]);
  const asked = searchParams.get('ville');
  const askedQueue = isReportQueue(searchParams.get('signalements')) ? searchParams.get('signalements') : null;
  const city = cities.find((one) => one.slug === asked) || cities[0] || null;
  const queueKey = `${city?.slug}|${askedQueue}`;

  const setParam = (name, value) => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set(name, value);
        return next;
      },
      { replace: true }
    );
  };

  const loadMandates = useCallback(async () => {
    try {
      const response = await apiCall('/moderator/mandates', { method: 'GET' });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(translateApiMessage(data.error) || 'Vos villes n’ont pas pu être chargées.');
      }

      setMandates(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setMandatesLoaded(true);
    }
  }, []);

  const loadQueue = useCallback(async () => {
    if (!city) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const query = `city=${encodeURIComponent(city.slug)}`;
      // The counts first: without a queue named, they decide which opens.
      const workloadResponse = await apiCall(`/moderator/workload?${query}`, { method: 'GET' });
      const workloadData = await workloadResponse.json().catch(() => ({}));
      const counts = workloadResponse.ok ? workloadData.data : null;
      const opened = askedQueue || firstReportQueue(counts?.reports_by_type);
      setReportQueue(opened);

      const queueResponse = await apiCall(`/moderator/reports/${opened}?${query}&limit=${QUEUE_LIMIT}`, {
        method: 'GET',
      });
      const queueData = await queueResponse.json().catch(() => ({}));

      if (!queueResponse.ok) {
        throw new Error(translateApiMessage(queueData.error) || 'La file n’a pas pu être chargée.');
      }

      setWorkload(counts);
      setQueue({ key: queueKey, items: queueData.data || [] });
      setError('');
    } catch (err) {
      setQueue({ key: queueKey, items: [] });
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [city, askedQueue, queueKey]);

  useEffect(() => {
    loadMandates();
  }, [loadMandates]);

  useEffect(() => {
    if (mandatesLoaded) {
      loadQueue();
    }
  }, [mandatesLoaded, loadQueue]);

  /**
   * One decision. Returns an error message, or an empty string when it went
   * through — the queue and the counts are read again either way it worked.
   */
  const decide = useCallback(
    async (path, body = null) => {
      const response = await apiCall(path, {
        method: 'POST',
        ...(null === body ? {} : { body: JSON.stringify(body) }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const violation = data.violations ? Object.values(data.violations)[0] : null;

        return translateApiMessage(violation || data.error, 'reason') || 'La décision n’a pas pu être enregistrée.';
      }

      await loadQueue();

      return '';
    },
    [loadQueue]
  );

  const reviewReport = (report, decision, reason = null) =>
    decide(`/moderator/reports/${reportQueue}/${report.uuid}/review`, { decision, reason });

  /** Ask to moderate the city one lives in: the move, seen from this side. */
  const requestMandate = async () => {
    const response = await apiCall('/moderator/mandates', { method: 'POST' });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));

      return translateApiMessage(data.error) || 'La demande n’a pas pu être envoyée.';
    }

    await loadMandates();

    return '';
  };

  return {
    cities,
    city,
    pending,
    mandatesLoaded,
    reportQueue,
    setReportQueue: (value) => setParam('signalements', value),
    setCity: (slug) => setParam('ville', slug),
    workload,
    items: queue.key === queueKey ? queue.items : [],
    loading: loading || (Boolean(city) && queue.key !== queueKey),
    error,
    reviewReport,
    requestMandate,
  };
}
